/**
 * DocumentComparisonProvider
 *
 * Context provider for managing shared state in the document comparison UI.
 * Handles hover state, locked highlights, page navigation, and citation mappings.
 */

import {
  createContext,
  useCallback,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type {
  Citation,
  ConfidenceScores,
  DocumentComparisonContextValue,
  DocumentComparisonState,
  ExtendAIExtractionOutput,
  PageDimensions,
} from './types';
import { buildCitationMappings } from './hooks/useCitationMapping';

// ============================================================================
// Context
// ============================================================================

export const DocumentComparisonContext =
  createContext<DocumentComparisonContextValue | null>(null);

// ============================================================================
// Provider Props
// ============================================================================

interface DocumentComparisonProviderProps {
  children: ReactNode;
  /** Extraction output from Extend AI (for citation mapping) */
  extractionOutput?: ExtendAIExtractionOutput;
  /** Callback when a field is hovered and we should navigate to its citation */
  onNavigateToPage?: (columnId: string, page: number) => void;
}

// ============================================================================
// Provider Component
// ============================================================================

export function DocumentComparisonProvider({
  children,
  extractionOutput,
  onNavigateToPage,
}: DocumentComparisonProviderProps) {
  // Core state
  const [state, setState] = useState<DocumentComparisonState>({
    hoveredFieldPath: null,
    hoveredCitation: null,
    lockedFieldPath: null,
    activePages: {},
    pageDimensions: {},
  });

  // Build citation mappings
  const { fieldToCitations, citationToField, fieldToConfidence } = useMemo(
    () => buildCitationMappings(extractionOutput),
    [extractionOutput],
  );

  // ========================================================================
  // Actions
  // ========================================================================

  const setHoveredField = useCallback(
    (path: string | null) => {
      setState((prev) => ({ ...prev, hoveredFieldPath: path }));

      // Auto-navigate to citation page if field has citations
      if (path && onNavigateToPage) {
        const citations = fieldToCitations.get(path);
        if (citations && citations.length > 0) {
          const firstCitation = citations[0];
          // Navigate to the page in the first document column
          // The column ID will be determined by the layout
          onNavigateToPage('document', firstCitation.page);
        }
      }
    },
    [fieldToCitations, onNavigateToPage],
  );

  const setHoveredCitation = useCallback((citation: Citation | null) => {
    setState((prev) => ({ ...prev, hoveredCitation: citation }));
  }, []);

  const setLockedField = useCallback((path: string | null) => {
    setState((prev) => ({
      ...prev,
      // Toggle off if clicking the same field
      lockedFieldPath: prev.lockedFieldPath === path ? null : path,
    }));
  }, []);

  const navigateToPage = useCallback(
    (columnId: string, page: number) => {
      setState((prev) => ({
        ...prev,
        activePages: { ...prev.activePages, [columnId]: page },
      }));
      onNavigateToPage?.(columnId, page);
    },
    [onNavigateToPage],
  );

  const setPageDimensions = useCallback(
    (columnId: string, page: number, dimensions: PageDimensions) => {
      setState((prev) => ({
        ...prev,
        pageDimensions: {
          ...prev.pageDimensions,
          [columnId]: {
            ...prev.pageDimensions[columnId],
            [page]: dimensions,
          },
        },
      }));
    },
    [],
  );

  const getFieldForCitation = useCallback(
    (citation: Citation): string | null => {
      return citationToField.get(citation) ?? null;
    },
    [citationToField],
  );

  const getCitationsForField = useCallback(
    (fieldPath: string): Citation[] => {
      return fieldToCitations.get(fieldPath) ?? [];
    },
    [fieldToCitations],
  );

  const getConfidenceForField = useCallback(
    (fieldPath: string): ConfidenceScores | null => {
      return fieldToConfidence.get(fieldPath) ?? null;
    },
    [fieldToConfidence],
  );

  // ========================================================================
  // Context Value
  // ========================================================================

  const contextValue = useMemo<DocumentComparisonContextValue>(
    () => ({
      ...state,
      setHoveredField,
      setHoveredCitation,
      setLockedField,
      navigateToPage,
      setPageDimensions,
      getFieldForCitation,
      getCitationsForField,
      getConfidenceForField,
    }),
    [
      state,
      setHoveredField,
      setHoveredCitation,
      setLockedField,
      navigateToPage,
      setPageDimensions,
      getFieldForCitation,
      getCitationsForField,
      getConfidenceForField,
    ],
  );

  return (
    <DocumentComparisonContext.Provider value={contextValue}>
      {children}
    </DocumentComparisonContext.Provider>
  );
}
